// Normalising the server's workflow node list for display and for the node palette.
//
// The endpoint returns `{ categories, nodes, count, version }`. Each node entry carries an
// explicit `handles` object, `default_data`, `layout`, `category_color`, an `icon` (raw SVG)
// and per-parameter `control` hints. This module turns that into a stable shape the renderer
// and the palette both consume, while staying tolerant of the older, looser shapes.

const asArray = (value) => (Array.isArray(value) ? value : null);

const firstString = (...values) =>
  values.find((value) => typeof value === "string" && value.trim() !== "") ?? null;

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Find the node list inside whatever the endpoint returned.
 *
 * Accepts a bare array, `{ nodes: [] }`, `{ nodes: { key: {...} } }`, `{ data: [] }`,
 * `{ node_types: [] }`, `{ catalog: [] }`.
 */
export const extractNodeList = (payload) => {
  const direct = asArray(payload);
  if (direct) return direct;

  if (!payload || typeof payload !== "object") return [];

  for (const key of ["nodes", "node_types", "nodeTypes", "data", "catalog", "items", "available"]) {
    const value = payload[key];

    const list = asArray(value);
    if (list) return list;

    if (value && typeof value === "object") {
      return Object.entries(value).map(([mapKey, entry]) =>
        entry && typeof entry === "object" ? { key: mapKey, ...entry } : { key: mapKey, name: String(entry) }
      );
    }
  }

  return [];
};

/**
 * The category list, normalised to `{ id, label, color, order }`.
 */
export const extractCategories = (payload) => {
  const list = asArray(payload?.categories);
  if (!list) return [];

  return list
    .map((category, index) => ({
      id: category?.id ?? `category-${index}`,
      label: category?.label ?? category?.id ?? String(index),
      color: category?.color ?? null,
      order: toNumber(category?.order, index),
    }))
    .sort((a, b) => a.order - b.order);
};

const normalizeHandles = (node) => {
  const handles = node?.handles && typeof node.handles === "object" ? node.handles : {};

  const mapHandle = (handle, index) => ({
    id: firstString(handle?.id, handle?.key) ?? `handle-${index}`,
    label: firstString(handle?.label, handle?.id) ?? `handle-${index}`,
    position: firstString(handle?.position) ?? "left",
    type: firstString(handle?.type) ?? "target",
  });

  return {
    inputs: asArray(handles.inputs)?.map(mapHandle) ?? [],
    outputs: asArray(handles.outputs)?.map(mapHandle) ?? [],
    dynamicInputs: Boolean(handles.dynamic_inputs),
    dynamicOutputs: Boolean(handles.dynamic_outputs),
    inputSource: firstString(handles.input_source) ?? null,
    outputSource: firstString(handles.output_source) ?? null,
    inputHandleField: firstString(handles.dynamic_input_handle_field, handles.input_handle_field) ?? null,
    outputHandleField: firstString(handles.dynamic_output_handle_field, handles.output_handle_field) ?? null,
    fallbackHandle: firstString(handles.fallback_handle) ?? null,
  };
};

/**
 * Shape one node entry.
 *
 * @param {object} entry Raw entry, or a pre-normalised one.
 * @param {number} index Position in the list, for stable keys.
 * @returns A rich definition: key/type, name, category/colour, icon, layout, handles,
 *   defaultData, parameters, requires, and the untouched raw entry.
 */
export const normalizeWorkflowNode = (entry, index = 0) => {
  const node = entry && typeof entry === "object" ? entry : { name: String(entry) };

  const type = String(firstString(node.type, node.key, node.id) ?? `node-${index}`);

  const icon = node.icon && typeof node.icon === "object" ? node.icon : null;

  return {
    key: type,
    type,
    name: firstString(node.label, node.title, node.name) ?? type,
    category: firstString(node.category, node.group, node.kind) ?? null,
    categoryColor: firstString(node.category_color) ?? null,
    // Entry/terminal flags: the sink (ChromaDB) is a terminal even though its category is
    // "ingestion", and it renders like an output node.
    isEntry: node.is_entry === true,
    isTerminal: node.is_terminal === true,
    description: firstString(node.description, node.summary, node.details) ?? null,
    notes: firstString(node.notes) ?? null,
    role: firstString(node.role) ?? null,
    icon,
    layout: {
      width: toNumber(node.layout?.width, 280),
      height: toNumber(node.layout?.height, 160),
    },
    handles: normalizeHandles(node),
    defaultData: node.default_data && typeof node.default_data === "object" ? node.default_data : {},
    // Machine-readable builder metadata: capabilities endpoint, key form, platform-key notice.
    builder: node.builder && typeof node.builder === "object" ? node.builder : {},
    parameters: asArray(node.parameters) ?? [],
    requires: asArray(node.requires) ?? [],
    capabilities: asArray(node.capabilities) ?? [],
    useWhen: asArray(node.use_when) ?? [],
    order: toNumber(node.order, index),
    raw: node,
  };
};

export const normalizeWorkflowNodes = (payload) =>
  extractNodeList(payload).map((entry, index) => normalizeWorkflowNode(entry, index));

/**
 * Group normalised nodes under their categories, in category order (nodes by their `order`).
 *
 * @param {object[]} nodes Normalised nodes.
 * @param {{id:string,label:string,color:string|null,order:number}[]} categories
 * @returns {{category: object, nodes: object[]}[]} One group per category that has nodes.
 */
export const groupNodesByCategory = (nodes = [], categories = []) => {
  const order = new Map(categories.map((category, index) => [category.id, index]));
  const groups = new Map();

  (nodes || []).forEach((node) => {
    const categoryId = node.category ?? "uncategorised";
    if (!groups.has(categoryId)) {
      const known = categories.find((category) => category.id === categoryId);
      groups.set(categoryId, {
        category: known ?? { id: categoryId, label: categoryId, color: null, order: Infinity },
        nodes: [],
      });
    }
    groups.get(categoryId).nodes.push(node);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      nodes: [...group.nodes].sort((a, b) => a.order - b.order),
    }))
    .sort((a, b) => {
      const ao = order.has(a.category.id) ? order.get(a.category.id) : Number.MAX_SAFE_INTEGER;
      const bo = order.has(b.category.id) ? order.get(b.category.id) : Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
};

export default normalizeWorkflowNodes;
